import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ChevronDown, RefreshCw, RotateCcw, Trash2, X } from "lucide-react";
import { Button, Card, CardContent, PageContent, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "beproduct-ui";
import { api, isApiError } from "@/lib/api";
import { PageHeader } from "@/components/navigation/page-header";

interface QuarantineRow {
  master_folder: string;
  entity_type: string;
  stage: string;
  entity_id: string;
  sub_id: string;
  error_class: string;
  error_text: string | null;
  attempts: number;
  first_seen_at: string;
  last_seen_at: string;
}

/**
 * Mirrors `QuarantineVerdict` in src/api/quarantine-verdict.ts, the way
 * QuarantineRow above mirrors that endpoint's row shape: the web layer states
 * what it consumes rather than importing across the API boundary.
 */
type QuarantineVerdict =
  | "recovered"
  | "rearmed"
  | "deleted_upstream"
  | "still_failing"
  | "skipped"
  | "not_found"
  | "error";

interface QuarantineRetryOneResponse {
  ok: boolean;
  verdict: QuarantineVerdict;
  message: string;
}

interface QuarantineResponse {
  ok: boolean;
  /** false when the warehouse predates the quarantine table. */
  present: boolean;
  total?: number;
  rows: QuarantineRow[];
  warehouse_error?: string;
}

export const Route = createFileRoute("/_authed/$companySlug/quarantine")({
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug);
  },
  component: QuarantinePage,
});

function fmt(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toISOString().replace("T", " ").slice(0, 19);
}

/** Attempts is the number that matters: 1 is noise, 6 is a real problem. */
function attemptsTone(n: number): string {
  if (n >= 5) return "text-red-600 dark:text-red-400 font-semibold";
  if (n >= 3) return "text-amber-600 dark:text-amber-400 font-medium";
  return "text-muted-foreground";
}

/** Stable identity of a queue row: its primary key, minus the tenant. */
function rowKey(r: QuarantineRow): string {
  return `${r.master_folder}-${r.entity_type}-${r.entity_id}-${r.sub_id}`;
}

interface RowRetryState {
  /** While the request is in flight. */
  pending: boolean;
  verdict?: QuarantineVerdict;
  message?: string;
}

/**
 * How each verdict reads at a glance — a retry that failed must look different
 * from one that worked without having to read the sentence.
 */
const VERDICT_STYLE: Record<QuarantineVerdict, { label: string; className: string }> = {
  recovered: { label: "Recovered", className: "text-green-600 dark:text-green-400" },
  deleted_upstream: { label: "Deleted upstream", className: "text-muted-foreground" },
  rearmed: { label: "Re-armed", className: "text-blue-600 dark:text-blue-400" },
  still_failing: { label: "Still failing", className: "text-amber-600 dark:text-amber-400" },
  skipped: { label: "Skipped", className: "text-muted-foreground" },
  not_found: { label: "Already gone", className: "text-muted-foreground" },
  error: { label: "Retry error", className: "text-red-600 dark:text-red-400" },
};

/**
 * Beyond this many characters an error is collapsed to one line.
 *
 * The two errors that actually fill this queue are ~200 characters (a .NET
 * exception with its stack frame) and wrap to four or five lines each, so a
 * screenful of rows became a page of scrolling. Short errors are left alone —
 * a toggle on "bad json" is just another thing to click.
 */
const ERROR_CLAMP_CHARS = 90;

/** The error text: one line with a toggle when it is long, verbatim when not. */
function ErrorCell({
  text,
  expanded,
  onToggle,
}: {
  text: string | null;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (!text) return <span>—</span>;
  if (text.length <= ERROR_CLAMP_CHARS) return <span className="break-words">{text}</span>;

  return (
    <div className="flex items-start gap-1">
      <span
        className={`min-w-0 flex-1 ${expanded ? "break-words" : "block truncate"}`}
        title={expanded ? undefined : text}
      >
        {text}
      </span>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse error" : "Show the full error"}
        className="shrink-0 rounded p-0.5 hover:bg-muted text-muted-foreground"
      >
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
}

/** Verdicts after which the record is no longer queued upstream of a refresh. */
const RESOLVED: ReadonlySet<QuarantineVerdict> = new Set([
  "recovered",
  "deleted_upstream",
  "not_found",
]);

/** One row's retry outcome, in its own column. */
function RowResult({ state }: { state?: RowRetryState }) {
  if (!state) return null;
  if (state.pending) {
    return (
      <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
        <RefreshCw className="h-3 w-3 animate-spin" />
        Retrying…
      </span>
    );
  }
  if (!state.verdict) return null;
  const style = VERDICT_STYLE[state.verdict];
  return (
    <span className={`text-xs font-medium ${style.className}`} title={state.message}>
      {style.label}
    </span>
  );
}

function QuarantinePage() {
  const { company } = Route.useRouteContext();
  const { companySlug } = Route.useParams();

  const q = useQuery<QuarantineResponse>({
    queryKey: ["quarantine", company.id],
    // The `/api` prefix is explicit: api() passes any path starting with "/"
    // straight through, so "/tenants/..." would hit the SPA and return HTML.
    queryFn: () => api<QuarantineResponse>(`/api/tenants/${company.id}/quarantine`),
    refetchOnWindowFocus: false,
  });

  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["quarantine", company.id] });
  const [clearAllOpen, setClearAllOpen] = React.useState(false);
  const [clearedCount, setClearedCount] = React.useState<number | null>(null);
  const workflowState = useQuery({
    queryKey: ["tb-rebuild-maintenance", company.id],
    queryFn: () => api<{ maintenance: boolean; remainingRuns: number }>(
      `/api/tenants/${company.id}/tb-rebuild-maintenance`,
    ),
    refetchInterval: 3_000,
  });
  const clearAll = useMutation({
    mutationFn: () => api<{ ok: boolean; cleared: number }>(
      `/api/tenants/${company.id}/quarantine/clear-all`,
      { method: "POST" },
    ),
    onSuccess: (result) => {
      setClearAllOpen(false);
      setClearedCount(result.cleared);
      setRowRetries({});
      setLastOutcome(null);
      void invalidate();
      void workflowState.refetch();
    },
    onError: () => {
      void workflowState.refetch();
      void invalidate();
    },
  });

  /** Re-attempt everything now instead of waiting for the next sync. */
  const retryAll = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; results: { master_folder?: string; recovered?: number; rearmed?: number; still_failing?: number }[] }>(
        `/api/tenants/${company.id}/quarantine/retry`,
        { method: "POST" },
      ),
    onSuccess: invalidate,
  });

  /** Give up on one record — stops the retries and the amber. */
  const clearOne = useMutation({
    mutationFn: (r: QuarantineRow) =>
      api<{ ok: boolean; cleared: number }>(`/api/tenants/${company.id}/quarantine/clear`, {
        method: "POST",
        body: JSON.stringify({
          masterFolder: r.master_folder,
          entityId: r.entity_id,
          subId: r.sub_id,
        }),
      }),
    onSuccess: invalidate,
  });

  /**
   * Per-row retry state, keyed by row identity rather than index — the list
   * re-sorts worst-first on every refetch, so an index would attach the result
   * to whichever row moved into that slot.
   */
  const [rowRetries, setRowRetries] = React.useState<Record<string, RowRetryState>>({});
  /** Which rows are showing their error in full. */
  const [expandedErrors, setExpandedErrors] = React.useState<Record<string, boolean>>({});
  const toggleError = (key: string) =>
    setExpandedErrors((prev) => ({ ...prev, [key]: !prev[key] }));

  /** Outlives the row: a recovered record leaves the table on the next refetch. */
  const [lastOutcome, setLastOutcome] = React.useState<
    { entityId: string; verdict: QuarantineVerdict; message: string } | null
  >(null);

  /** Re-attempt one record and show what happened to it. */
  async function retryRow(r: QuarantineRow) {
    const key = rowKey(r);
    setRowRetries((prev) => ({ ...prev, [key]: { pending: true } }));
    try {
      const res = await api<QuarantineRetryOneResponse>(`/api/tenants/${company.id}/quarantine/retry`, {
        method: "POST",
        body: JSON.stringify({
          masterFolder: r.master_folder,
          entityId: r.entity_id,
          subId: r.sub_id,
        }),
      });
      setRowRetries((prev) => ({ ...prev, [key]: { pending: false, verdict: res.verdict, message: res.message } }));
      setLastOutcome({ entityId: r.entity_id, verdict: res.verdict, message: res.message });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setRowRetries((prev) => ({ ...prev, [key]: { pending: false, verdict: "error", message } }));
      setLastOutcome({ entityId: r.entity_id, verdict: "error", message });
    }
    // Deliberately NO refetch. A retried row is usually resolved, so reloading
    // would delete the row the operator is reading the answer off — the result
    // has to outlast the action that produced it. Refresh reloads the queue
    // when they are ready.
  }

  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? rows.length;
  const rowRetryPending = Object.values(rowRetries).some((state) => state.pending);
  const blockedByWorkflow = !workflowState.data || workflowState.isError ||
    workflowState.data.maintenance || workflowState.data.remainingRuns > 0;
  const clearAllDisabled = !q.data?.ok || !q.data.present || q.isError || q.isFetching || total === 0 ||
    blockedByWorkflow || workflowState.isFetching || clearAll.isPending ||
    retryAll.isPending || clearOne.isPending || rowRetryPending;
  const clearErrorCode = isApiError(clearAll.error)
    ? (clearAll.error.body as { error?: string } | null)?.error
    : undefined;
  const clearErrorText = clearErrorCode === "active_workflows"
    ? "A workflow is now queued, running, or cancelling. Wait for it to finish before clearing."
    : clearErrorCode === "tenant_maintenance_mode"
      ? "Warehouse maintenance is active. Clear All is blocked until maintenance ends."
      : clearErrorCode === "quarantine_clear_failed"
        ? "The warehouse did not confirm deletion. Refresh the queue before retrying."
        : clearAll.error instanceof Error ? clearAll.error.message : String(clearAll.error);
  const retrySummary = retryAll.data?.results
    ?.filter((r) => (r.recovered ?? 0) + (r.rearmed ?? 0) + (r.still_failing ?? 0) > 0)
    .map((r) => `${(r.master_folder ?? "").toLowerCase()}: ${r.recovered ?? 0} recovered, ${r.rearmed ?? 0} re-armed, ${r.still_failing ?? 0} still failing`)
    .join(" · ");

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 space-y-6">
      <PageHeader title="Quarantine" subtitle={<>Records the sync could not process. This is a retry queue — everything here is attempted again on the next run, and clears itself on success. What matters is <strong>attempts</strong>: 1 is noise, a record failing every run needs a look.</>} actions={
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={() => q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${q.isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            onClick={() => retryAll.mutate()}
            disabled={retryAll.isPending || rows.length === 0}
            title="Re-attempt every queued record now, instead of waiting for the next sync"
          >
            <RotateCcw className={`h-4 w-4 mr-2 ${retryAll.isPending ? "animate-spin" : ""}`} />
            {retryAll.isPending ? "Retrying…" : "Retry all"}
          </Button>
          <Button
            variant="outline"
            className="hover:!border-[var(--destructive)] hover:!bg-[var(--destructive)] hover:!text-white dark:hover:!bg-[var(--destructive)]"
            onClick={() => { clearAll.reset(); setClearAllOpen(true); }}
            disabled={clearAllDisabled}
            title={blockedByWorkflow ? "Unavailable while workflows are queued, running, cancelling, or maintenance is active" :
              "Permanently remove every quarantine record for this tenant"}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All
          </Button>
        </div>
      } />

      <Dialog open={clearAllOpen} onOpenChange={(open) => { if (!clearAll.isPending) setClearAllOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear all quarantine records · {company.name}?</DialogTitle>
            <DialogDescription>
              Permanently remove {total} quarantine record{total === 1 ? "" : "s"} from this
              tenant, including all fetch and pivot failures not displayed in the table.
              This stops their pending retries; it does not delete source data or typed
              tables. Future syncs may quarantine failing records again. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {blockedByWorkflow && (
            <p className="text-sm text-destructive" role="alert">
              Clear All is unavailable while a workflow is queued, running, cancelling,
              or warehouse maintenance is active.
            </p>
          )}
          {clearAll.isError && (
            <p className="text-sm text-destructive" role="alert">
              Clear All failed: {clearErrorText}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={clearAll.isPending} onClick={() => setClearAllOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={clearAllDisabled}
              onClick={() => clearAll.mutate()}>
              {clearAll.isPending ? "Clearing…" : "Clear All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {clearedCount !== null && (
        <div className="rounded-lg border px-4 py-3 text-sm" role="status">
          Quarantine cleared: {clearedCount} record{clearedCount === 1 ? "" : "s"} deleted.
        </div>
      )}

      {/* `isLoading` alone is not enough on react-query v5: a query that is
          pending-but-not-loading renders nothing at all. Mirror the pattern
          used in schema.tsx. */}
      {(q.isLoading || (!q.data && q.isFetching)) && (
        <p className="text-sm text-muted-foreground mt-6">Loading…</p>
      )}

      {/* A failed request must say so. Without this branch the page renders its
          header and nothing else, which reads as "no data" — the opposite of
          the truth. */}
      {q.isError && !q.isFetching && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <p className="text-sm text-red-600 dark:text-red-400">
              Could not load the quarantine queue:{" "}
              {q.error instanceof Error ? q.error.message : String(q.error)}
            </p>
          </CardContent>
        </Card>
      )}

      {q.data?.warehouse_error && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <p className="text-sm text-red-600 dark:text-red-400">
              Could not read the warehouse: {q.data.warehouse_error}
            </p>
          </CardContent>
        </Card>
      )}

      {q.data && q.data.ok && !q.data.present && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              This warehouse has no <code>be_sync_quarantine</code> table yet — it appears the
              first time <code>init_database</code> runs after the quarantine feature shipped.
            </p>
          </CardContent>
        </Card>
      )}

      {q.data?.present && rows.length === 0 && (
        <Card className="mt-6">
          <CardContent className="pt-6 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-sm">Nothing quarantined. Every record synced.</p>
          </CardContent>
        </Card>
      )}

      {retryAll.isError && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <p className="text-sm text-red-600 dark:text-red-400">
              Retry failed:{" "}
              {retryAll.error instanceof Error ? retryAll.error.message : String(retryAll.error)}
            </p>
          </CardContent>
        </Card>
      )}

      {retryAll.isSuccess && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <p className="text-sm">
              {retrySummary || "Nothing to retry."}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              &ldquo;Re-armed&rdquo; means a pivot failure was queued for the next sync rather than
              retried on the spot — one row cannot be pivoted in isolation.
            </p>
          </CardContent>
        </Card>
      )}

      {lastOutcome && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <p className="text-sm">
              <span className={`font-medium ${VERDICT_STYLE[lastOutcome.verdict].className}`}>
                {VERDICT_STYLE[lastOutcome.verdict].label}
              </span>{" "}
              · <code className="text-xs">{lastOutcome.entityId}</code>
            </p>
            <p className="text-sm text-muted-foreground mt-1">{lastOutcome.message}</p>
            <p className="text-xs text-muted-foreground mt-2">
              The table below is not reloaded automatically, so results stay where you can read
              them. Use <strong>Refresh</strong> when you want the current queue.
            </p>
          </CardContent>
        </Card>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex items-center gap-2 mt-6 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <span className="text-sm">
              {total} record{total === 1 ? "" : "s"} outstanding
              {total > rows.length ? ` (showing first ${rows.length})` : ""}
            </span>
          </div>
          <Card>
            <CardContent className="pt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-2 pr-4 font-medium">Attempts</th>
                    <th className="pb-2 pr-4 font-medium">Entity</th>
                    <th className="pb-2 pr-4 font-medium">Stage</th>
                    <th className="pb-2 pr-4 font-medium">ID</th>
                    <th className="pb-2 pr-4 font-medium">Sub-ID</th>
                    <th className="pb-2 pr-4 font-medium">Class</th>
                    <th className="pb-2 pr-4 font-medium">Error</th>
                    <th className="pb-2 pr-4 font-medium">Last seen</th>
                    <th className="pb-2 pr-4 font-medium">Result</th>
                    <th className="pb-2 font-medium sr-only">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={rowKey(r)}
                      className={`border-b last:border-0 align-top ${
                        RESOLVED.has(rowRetries[rowKey(r)]?.verdict as QuarantineVerdict)
                          ? "opacity-50"
                          : ""
                      }`}
                    >
                      <td className={`py-2 pr-4 tabular-nums ${attemptsTone(r.attempts)}`}>
                        {r.attempts}
                      </td>
                      <td className="py-2 pr-4">
                        {r.master_folder.toLowerCase()} · {r.entity_type}
                      </td>
                      <td className="py-2 pr-4">{r.stage}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{r.entity_id}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{r.sub_id || "—"}</td>
                      <td className="py-2 pr-4">{r.error_class}</td>
                      <td className="py-2 pr-4 max-w-md text-muted-foreground">
                        <ErrorCell
                          text={r.error_text}
                          expanded={Boolean(expandedErrors[rowKey(r)])}
                          onToggle={() => toggleError(rowKey(r))}
                        />
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                        {fmt(r.last_seen_at)}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        <RowResult state={rowRetries[rowKey(r)]} />
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => retryRow(r)}
                            disabled={rowRetries[rowKey(r)]?.pending || retryAll.isPending}
                            title="Re-attempt just this record now and show what happened"
                          >
                            <RotateCcw
                              className={`h-4 w-4 mr-1 ${rowRetries[rowKey(r)]?.pending ? "animate-spin" : ""}`}
                            />
                            Retry
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => clearOne.mutate(r)}
                            disabled={clearOne.isPending}
                            title="Give up on this record: stop retrying it and stop it holding runs at partial"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Clear
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground mt-3">
            <strong>Class</strong> tells you what kind of failure it was:{" "}
            <code>data</code> — the payload could not be parsed and the record was isolated by
            narrowing the request; <code>pivot</code> — it fetched fine but could not be written to
            the typed tables; <code>transport</code> — quarantined while the API was unwell, so it
            is likely to clear on its own.
          </p>
        </>
      )}
      </div>
    </PageContent>
  );
}
